import { useEffect, useState, useMemo, useCallback } from 'react';
import { CoreBanner, type CoreBannerProps } from '@vitvio/design-system';
import { usePersistentState } from '@vitvio/helpers';
import { getLoggerSafe } from '@vitvio/logging';
import { STORAGE_KEYS } from '@config/constants';
import dayjs from 'dayjs';

interface UpdateBannerProps {
  currentBuildId: string;
}

const isDevMode = process.env.NODE_ENV === 'development';

type BannerState = 'hidden' | 'updateAvailable' | 'updateFailed';

export function UpdateBanner({ currentBuildId }: UpdateBannerProps) {
  const logger = getLoggerSafe();
  const [bannerState, setBannerState] = useState<BannerState>('hidden');
  const [checksShouldRun, setChecksShouldRun] = useState(true);
  const [lastCheck, setLastCheck] = usePersistentState<string | null>(null, STORAGE_KEYS.LAST_VERSION_CHECK_TIMESTAMP);
  const [updateAttempted, setUpdateAttempted] = usePersistentState<boolean>(
    false,
    STORAGE_KEYS.VERSION_UPDATE_ATTEMPTED,
    'sessionStorage',
  );

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isDevMode) {
      // Suppress the version check in development mode
      return;
    }

    const checkForNewVersion = async () => {
      try {
        if (!lastCheck || dayjs().diff(dayjs(lastCheck), 'hour') >= 1) {
          const response = await fetch(`/version.json?t=${dayjs().valueOf()}`);
          if (!response.ok) {
            logger?.error(`Failed to fetch version.json: ${response.status}`, { context: 'checkForNewVersion' });
            return;
          }
          const { buildId } = await response.json();

          if (buildId && buildId !== currentBuildId) {
            logger?.info('New version available', { currentBuildId, fetchedBuildId: buildId });

            if (updateAttempted) {
              // Reload was attempted but version still mismatches - show failure message
              setUpdateAttempted(false);
              setBannerState('updateFailed');
            } else {
              // First time seeing this version mismatch
              setBannerState('updateAvailable');
            }

            setChecksShouldRun(false); // Stop further checks if mismatch is found
            clearInterval(intervalId);
          } else {
            // Version matches - clear any pending update attempt flag
            setUpdateAttempted(false);
          }
        }
      } catch (error) {
        logger?.error(error as Error, { context: 'checkForNewVersion', currentBuildId });
      }
    };

    const startVersionCheckInterval = () => {
      if (checksShouldRun) {
        intervalId = setInterval(checkForNewVersion, 5 * 60 * 1000);
      }
    };

    // Run the check immediately on mount
    checkForNewVersion();
    startVersionCheckInterval();

    // Clean up the interval on component unmount
    return () => clearInterval(intervalId);
  }, [currentBuildId, checksShouldRun, logger, lastCheck, updateAttempted, setUpdateAttempted]);

  const handleReload = useCallback(async () => {
    // Mark that we're attempting an update before reload
    setUpdateAttempted(true);

    // Properly await all cache deletions before reload
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    }

    window.location.reload();
  }, [setUpdateAttempted]);

  const handleRemindLater = useCallback(() => {
    setLastCheck(dayjs().toISOString());
    setBannerState('hidden');
    setChecksShouldRun(true); // Allow checks to run again after 1 hour
    setTimeout(
      () => {
        setChecksShouldRun(true);
      },
      60 * 60 * 1000,
    ); // 1 hour
  }, [setLastCheck]);

  const handleDismiss = useCallback(() => {
    setLastCheck(dayjs().toISOString());
    setBannerState('hidden');
    setChecksShouldRun(true);
  }, [setLastCheck]);

  const bannerProps = useMemo((): Omit<CoreBannerProps, 'visible'> => {
    if (bannerState === 'updateFailed') {
      return {
        type: 'warning',
        icon: 'warning',
        titlePath: 'common:update.failed.title',
        descriptionPath: 'common:update.failed.description',
        buttons: [
          {
            label: 'common:button.dismiss',
            type: 'secondary',
            onClick: handleDismiss,
          },
        ],
      };
    }

    return {
      type: 'info',
      icon: 'info',
      titlePath: 'common:update.available.title',
      descriptionPath: 'common:update.available.description',
      buttons: [
        {
          label: 'common:button.reload',
          type: 'secondary',
          onClick: handleReload,
        },
        {
          label: 'common:button.remindInOneHour',
          type: 'ghost',
          onClick: handleRemindLater,
        },
      ],
    };
  }, [bannerState, handleDismiss, handleReload, handleRemindLater]);

  if (isDevMode) {
    return null;
  }

  return <CoreBanner visible={bannerState !== 'hidden'} {...bannerProps} />;
}

export default UpdateBanner;
